import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StorageLocation } from 'apis/GraphQL/Variables/product';
import ProductApi from 'apis/ProductApis';
import { ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import FilterProductSloc from './FilterProductSloc';
import TableProductSloc from './TableProductSloc';

export interface PageInfo {
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export interface ProductData {
  pageInfo: PageInfo;
  items: StorageLocation[];
}

interface User {
  isSale?: boolean;
  isCN?: boolean;
  plantCode?: string;
}

const ProductSlocListPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { setTitle, setBreadcrumb } = useTitle();
  const [data, setData] = useState<ProductData>();
  const [filterData, setFilterData] = useState<Partial<StorageLocation>>({});
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const tableRef = useRef<any>(null);

  const queryParam = useQueryParam();

  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size = parseInt(queryParam.get('page_size') + '') || 10;

  const defaultFilter = useMemo(() => {
    // return user?.isSale || user?.isCN
    //   ? { plantId: (user as User)?.plantCode }
    //   : {};
    return user?.plantCode === '5000'
      ? {}
      : { plantId: (user as User)?.plantCode };
  }, [user]);

  const { isPending: isFetching, mutate: refetchFilter } = useMutation({
    mutationFn: (
      params: Partial<StorageLocation> & { types?: string; headerRow?: any },
    ) => {
      const mergedParams = { ...defaultFilter, ...filterData, ...params };
      return params?.types === 'export'
        ? ProductApi.getProductByFilter(mergedParams)
        : ProductApi.getProductByFilter({ ...defaultFilter, ...params });
    },
    onSuccess: (res, variable) => {
      const data = res.data.getProductList;

      const items: StorageLocation[] = data.items.map((item: any) => {
        return {
          ...item,
        };
      });

      const pageInfo: PageInfo = res.data.getProductList.pageInfo;
      const newData: ProductData = { items, pageInfo };

      if (variable.types === 'export') {
        exportExcel_v2(
          variable.headerRow,
          items || [],
          'Danh sách sản phẩm',
          true,
          2,
        );
      } else {
        setData(newData);
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  useEffect(() => {
    const params = !Object.keys(filterData).length
      ? { skip: (page - 1) * page_size, take: page_size, ...defaultFilter }
      : {
          skip: (page - 1) * page_size,
          take: page_size,
          ...defaultFilter,
          ...filterData,
        };
    refetchFilter(params);
  }, [filterData, page, page_size, defaultFilter, refetchFilter]);

  useEffect(() => {
    setTitle('Sản phẩm tồn kho theo Sloc');
    setBreadcrumb([
      { title: 'Sản phẩm' },
      { title: 'Sản phẩm tồn kho theo Sloc' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const handleExportExcel = useCallback(() => {
    if (tableRef.current) {
      tableRef.current.handleExportExcel();
    }
  }, []);

  const handleOkFiltered = (values: any) => {
    navigate(`?page=1&page_size=${page_size}`);
    setFilterData(values);
  };

  const handleCancelFiltered = () => {
    navigate(`?page=1&page_size=${page_size}`);
    setFilterData({});
  };

  return (
    <>
      <Row gutter={[8, 16]} align="middle" justify="end">
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport onClick={handleExportExcel} />
        </Col>
      </Row>
      {isOpenFilter && (
        <FilterProductSloc
          defaultFilter={defaultFilter}
          handleOkFiltered={handleOkFiltered}
          handleCancelFiltered={handleCancelFiltered}
        />
      )}
      <TableProductSloc
        data={data}
        ref={tableRef}
        isLoading={isFetching}
        refetchFilter={refetchFilter}
        filterData={filterData}
      />
    </>
  );
};

export default ProductSlocListPage;
